import PageInfo from "@/components/PageInfo";
import { Container, chakra, Box, Text, Button, Icon, Flex } from "@chakra-ui/react";
import Navbar from "@/components/Navbar";
import ModalCheckoutForm from "@/components/plans/ModalCheckoutForm";
import { useClientsideUserData, withUserData } from "@/contexts/UserDataContext";
import Footer from "@/components/Footer";
import { createCheckeredBackground } from "@/utils/style";
import { useEffect, useState } from "react";
import { useAPI } from "@/contexts/APIContext";
import { useRouter } from "next/router";
import DiscountText from "@/components/plans/DiscountText";
import { clearDiscountCode, applyDiscountCode } from "@/utils/discount-code-client";
import { FaShare } from "react-icons/fa";
import CollegeAdvisorModal from "@/components/collegeadvisor/CollegeAdvisorModal";
import { getDiscountData } from "@/utils/discount-service";
import AffiliateProvider, { useAffiliateProvider } from "@/components/affiliate/AffiliateProvider";
import PlanDecisionTree from "@/components/plans/PlanDecisionTree";
import { Packages } from "@/components/plans/Packages";

export const TimeLeft = ({ date }) => {
  const calculateTimeLeft = () => {
    const now = new Date();
    const diff = date - now;
    const hours = Math.floor(diff / 1000 / 60 / 60);
    const minutes = Math.floor((diff / 1000 / 60) % 60);
    const seconds = Math.floor((diff / 1000) % 60);
    return { hours, minutes, seconds };
  };

  if (new Date() > date) {
    clearDiscountCode();
  }

  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  // Calculate how long until it expires
  useEffect(() => {
    const interval = setInterval(() => setTimeLeft(calculateTimeLeft()), 1000);
    setTimeLeft(calculateTimeLeft());
    return () => window.clearInterval(interval);
  }, []);

  // Don't show the time left if it's more than 3 days
  if (timeLeft.hours > 72) {
    return null;
  }

  return (
    <Text fontSize={{ base: "lg", md: "xl" }} textAlign="center" mt={2}>
      ⚡️ Hurry! This sale expires soon. You only have{" "}
      <b>
        <>
          {timeLeft.hours.toString().padStart(2, "0")}:
          {timeLeft.minutes.toString().padStart(2, "0")}:
          {timeLeft.seconds.toString().padStart(2, "0")}
        </>
      </b>{" "}
      left. ⚡️
    </Text>
  );
};

const DiscountCodeSelector = () => {
  const api = useAPI();
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [discountData, setDiscountData] = useState({});

  const { discountCode, discountPct, discountMultiplier, discountExpireAt } = discountData;

  const discountExpireAtDate = discountExpireAt
    ? new Date(discountData.discountExpireAt)
    : undefined;
  const displayDiscountCodeArea = false;

  const onShare = () => {
    window.navigator.share({ url: window.location.href });
  };

  const applyCode = async (code) => {
    setIsLoading(true);
    await applyDiscountCode(api, code, router.query.silent === "true");
    setIsLoading(false);
  };

  const onClickApply = () => {
    const code = window.prompt("Enter your discount code for some sweet savings! 😄");
    if (!code) {
      return;
    }
    return applyCode(code);
  };

  useEffect(() => {
    const localDiscountData = getDiscountData();
    setDiscountData(localDiscountData);
  }, []);

  if (discountCode) {
    return (
      <>
        {displayDiscountCodeArea && (
          <Box mb={{ base: 2, md: 10 }} bg="white" p={4} borderRadius="xl" border="1px solid black">
            <Text textAlign="center" fontSize={{ base: "xl", md: "2xl" }}>
              <DiscountText
                discountCode={discountCode}
                discountPct={discountPct}
                discountMultiplier={discountMultiplier}
              />
            </Text>
            <TimeLeft date={discountExpireAtDate} />
          </Box>
        )}
        <Box
          textAlign="center"
          display={{ base: "block", md: "none" }}
          bg="green.50"
          p={4}
          borderRadius="xl"
          mb={4}
          border="1px dashed"
          borderColor="green.700"
        >
          <Text fontWeight="bold" fontSize="lg">
            Moving to another device? 💻
          </Text>
          <Text mt={2}>
            Send yourself a link to this page and the discount code will be auto-applied.
          </Text>
          <Button onClick={onShare} colorScheme="green" mt={2} leftIcon={<Icon as={FaShare} />}>
            Send link to myself
          </Button>
        </Box>
      </>
    );
  }

  return (
    <Text textAlign="center" fontSize={{ base: "2xl", md: "2xl" }}>
      Got a discount code?
      <Button
        ml={2}
        variant="outline"
        isLoading={isLoading}
        colorScheme="black"
        onClick={onClickApply}
      >
        Apply a discount
      </Button>
    </Text>
  );
};

const DiscountAppliedText = () => {
  const router = useRouter();
  const [code, setCode] = useState();

  useEffect(() => {
    const { discountCode } = getDiscountData();
    if (code !== discountCode) {
      setCode(discountCode);
    }
  });

  if (!code) {
    return null;
  }

  return (
    <Text
      fontSize={{ base: "lg" }}
      maxW={800}
      margin={{ base: "20px", md: "20px auto" }}
      textAlign="center"
    >
      You have applied a discount code: <strong>{code}</strong>.{" "}
      <Text
        as="button"
        color="red.500"
        cursor="pointer"
        textDecoration="underline"
        onClick={async () => {
          await clearDiscountCode();
          await router.reload();
        }}
      >
        Remove
      </Text>
    </Text>
  );
};

const AffiliateMultiplierAppliedText = () => {
  const { multiplier } = useAffiliateProvider();

  if (!multiplier) {
    return null;
  }

  return (
    <Text
      fontSize={{ base: "lg" }}
      maxW={800}
      margin={{ base: "20px", md: "20px auto" }}
      textAlign="center"
    >
      You have an affiliate <strong>{multiplier} multiplier</strong>!
      <Text fontSize={{ base: "sm" }} maxW={800} textAlign="center">
        ** Automatically activated when no other discount is applied **
      </Text>
    </Text>
  );
};

const Plans = () => {
  useClientsideUserData();

  const bg = createCheckeredBackground();

  const [selectedProfilePlan, setSelectedProfilePlan] = useState(null);
  const [selectedEssayPlan, setSelectedEssayPlan] = useState(null);

  return (
    <AffiliateProvider>
      <PageInfo
        title="Packages"
        themeColor="beige"
        description="Premier profiles, right at your fingertips. Our prices are far more affordable than costly college consultants."
      />

      <Box {...bg} minH="100vh" pb={150}>
        <Navbar />

        <chakra.h1 mt={5} textAlign="center" textStyle="pageHeader" fontWeight={600}>
          Packages
        </chakra.h1>

        <Container maxW="container.xl">
          <Flex
            gap={5}
            mb={5}
            flexDir={{ base: "column-reverse", lg: "row" }}
            alignItems={{ base: "center", lg: "stretch" }}
          >
            <Box maxWidth={400} minWidth={250} mt={{ base: 5, lg: 0 }}>
              <PlanDecisionTree
                setSelectedProfilePlan={setSelectedProfilePlan}
                setSelectedEssayPlan={setSelectedEssayPlan}
              />
            </Box>

            <Box>
              <Packages
                selectedProfilePlan={selectedProfilePlan}
                setSelectedProfilePlan={setSelectedProfilePlan}
                selectedEssayPlan={selectedEssayPlan}
                setSelectedEssayPlan={setSelectedEssayPlan}
              />
            </Box>
          </Flex>

          <DiscountCodeSelector />

          <DiscountAppliedText />

          <AffiliateMultiplierAppliedText />

          <Text fontSize="sm" textAlign="center">
            Legal Notice: Admit Yogi LLC is not endorsed by or affiliated with the Common
            Application.
          </Text>
        </Container>
      </Box>

      <Box marginTop={{ base: -100, md: -170 }}>
        <Footer />
      </Box>

      <ModalCheckoutForm />
      <CollegeAdvisorModal />
    </AffiliateProvider>
  );
};

export default withUserData(Plans);
